import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <div className="privacy-policy">
      <nav className="privacy-nav">
        <div className="privacy-nav-content">
          <Link to="/" className="privacy-nav-home">
            <Logo size="small" showText={true} />
          </Link>
          <Link to="/" className="privacy-nav-back">
            ← Back to Converter
          </Link>
        </div>
      </nav>
      
      <div className="privacy-container">
        <h1 className="privacy-title">Privacy and Cookie Policy</h1>
        
        <div className="privacy-content">
          <section className="privacy-section">
            <p className="privacy-intro">
              At FileConvertHub, we're committed to protecting and respecting your privacy. This Policy explains when and why we collect personal information about people who visit our website, how we use it, the conditions under which we may disclose it to others, and how we keep it secure. This Policy also explains how we use cookies and similar technology on our website.
            </p>
            <p>
              We may change this Policy from time to time so please check this page occasionally to ensure that you're happy with any changes. By using our website, you're agreeing to be bound by this Policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>What Information Do We Collect?</h2>
            <p>
              FileConvertHub ("FileConvertHub", "we" or "us") collects (a) the e-mail addresses of those who communicate with us via e-mail, (b) aggregate information concerning what pages users access or visit, (c) information volunteered by the user (such as survey information and/or site registrations) and (d) information related to your use of the site, including IP address, geographic location, and date and time of your request.
            </p>
            <p>
              <strong>Important Note:</strong> Our file conversion service operates entirely in your browser using HTML5 technology. Your files are never uploaded to our servers - all conversion processing happens locally on your device. We do not have access to, store, or process your actual files.
            </p>
          </section>

          <section className="privacy-section">
            <h2>How Do We Use the Information?</h2>
            <p>FileConvertHub uses collected information for the following purposes:</p>
            <ul>
              <li>To fulfill a contract or take steps linked to a contract such as processing your registration on our website or sending you information about changes to our terms or policies;</li>
              <li>Where it is necessary for purposes which are in FileConvertHub's or third parties' legitimate interests such as:
                <ul>
                  <li>To provide the information or content you have requested</li>
                  <li>To contact you about our programs, products, features or services</li>
                  <li>For internal business purposes such as identification and authentication or customer service</li>
                  <li>To ensure the security of our website, by trying to prevent unauthorized or malicious activities</li>
                  <li>To enforce compliance with our terms of use and other policies</li>
                  <li>To help other organizations (such as copyright owners) to enforce their rights</li>
                  <li>To tailor content, advertisements and offers for you or for other purposes disclosed at the time of collection</li>
                </ul>
              </li>
            </ul>
            <p>
              If you do not wish to receive information about our programs, products, features or services, you may send an email to us at <a href="mailto:support@file-convert-hub.com">support@file-convert-hub.com</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>File Processing and Security</h2>
            <p>
              <strong>Your Privacy is Our Priority:</strong> Unlike many online conversion services, FileConvertHub processes all file conversions directly in your browser using advanced HTML5 and JavaScript technologies. This means:
            </p>
            <ul>
              <li>Your files never leave your device</li>
              <li>No files are uploaded to our servers</li>
              <li>No file content is stored, cached, or accessible to us</li>
              <li>All conversion processing happens locally on your computer</li>
              <li>You maintain complete control and ownership of your files</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How Do We Share Your Information?</h2>
            <p>
              We do not share or sell your personal data to other organizations for commercial purposes, except to provide products or services you've requested, when we have your permission, or under the following circumstances:
            </p>
            <ul>
              <li>It is necessary to share information in order to investigate, prevent, or take action regarding illegal activities, suspected fraud, situations involving potential threats to the physical safety of any person, violations of Terms of Service, or as otherwise required by law.</li>
              <li>We transfer information about you if FileConvertHub is acquired by or merged with another company. In this event, FileConvertHub will notify you before information about you is transferred and becomes subject to a different privacy policy.</li>
              <li>We provide such information to trusted businesses or persons for the sole purpose of processing personally identifying information on our behalf. When this is done, it is subject to agreements that oblige those parties to process such information only on our instructions and in compliance with this Privacy Policy and appropriate confidentiality and security measures.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Cookies and Web Beacons</h2>
            <p>
              A cookie is a small amount of data, which often includes an anonymous unique identifier, which is sent to your browser from a website's computers and stored on your computer's hard drive. Cookies are required to use some FileConvertHub services. FileConvertHub and its ad management partners ("Ad Partners") use cookies to record current session information.
            </p>
            <p>
              Our Ad Partners may also from time to time use web beacons (also known as Internet tags, pixel tags and clear GIFs). These web beacons are provided by our Ad Partners and allow Ad Partners to obtain information such as the IP address of the computer that downloaded the page on which the beacon appears, the URL of the page on which the beacon appears, the time the page containing the beacon was viewed, the type of browser used to view the page, and the information in cookies set by the Ad Partners.
            </p>
            <p>
              With both cookies and web beacon technology, the information that we collect and share is anonymous and not personally identifiable. It does not contain your name, address, telephone number, or email address.
            </p>
            <p>
              You can opt-out of Google Analytics data collection with the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
            </p>
            <p>
              You may also wish to refer to <a href="http://www.allaboutcookies.org/manage-cookies/" target="_blank" rel="noopener noreferrer">this website</a> for additional information about disabling cookies from your browser.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data Storage</h2>
            <p>
              FileConvertHub uses third party vendors and hosting partners to provide the necessary hardware, software, networking, storage, and related technology required to run FileConvertHub. FileConvertHub owns the code, databases, and all rights to the FileConvertHub application.
            </p>
            <p>
              <strong>Important:</strong> While we use third-party hosting for our website, your files are processed entirely in your browser and are never transmitted to or stored on our servers or any third-party servers.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Security</h2>
            <p>
              We take precautions to ensure the security of your personal information. However, we cannot guarantee that hackers or unauthorized personnel may gain access to your personal information despite our efforts. You should note that in using the FileConvertHub service, your information will travel over the Internet and through third party infrastructures which are not under our control.
            </p>
            <p>
              We cannot protect, nor does this Privacy Policy apply to, any information that you transmit to other users. You should never transmit personal or identifying information to other users.
            </p>
            <p>
              <strong>File Security:</strong> Since all file processing happens locally in your browser, your files benefit from the highest level of security - they never leave your device and are not accessible to anyone else, including us.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Retention of Your Personal Information</h2>
            <p>
              We retain information as long as it is necessary to provide the services requested by you and others, subject to any legal obligations to further retain such information. Information associated with your account will generally be kept until it is no longer necessary to provide the services or until you ask us to delete it or your account is deleted whichever comes first.
            </p>
            <p>
              Information about you that is no longer necessary and relevant to provide our services may be de-identified and aggregated with other non-personal data to provide insights which are commercially valuable to FileConvertHub, such as statistics related to the use of FileConvertHub's website.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Children</h2>
            <p>
              The FileConvertHub service is not intended for children under the age of 16, and we do not knowingly collect information from children under the age of 16.
            </p>
            <p>
              Children aged 16 or younger should not submit any personal information without the permission of their parents or guardians. By using the FileConvertHub service, you are representing that you are at least 18 years old, or that you are at least 16 years old and have your parents' or guardians' permission to use the service.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Consent to Worldwide Transfer and Processing of Personal Information</h2>
            <p>
              FileConvertHub is located in the United States. If you are not located in the United States, by accessing the website and providing personal information through it, you agree and acknowledge and consent to the collection, maintenance, processing and transfer of such information in and to the United States and other countries and territories.
            </p>
            <p>
              These other jurisdictions may have different privacy laws from your home jurisdiction and provide different levels of protection of personal information. FileConvertHub does have processes and procedures in place to provide adequate levels of protection to protect the transfer of data from the European Union.
            </p>
          </section>

          <section className="privacy-section">
            <h2>EU and EEA Users' Rights</h2>
            <p>
              If you are habitually located in the European Union or European Economic Area, you have the right to access, rectify, download or erase your information, as well as the right to restrict and object to certain processing of your information. While some of these rights apply generally, certain rights apply only in certain limited circumstances.
            </p>
            <p>
              You have the right to access your personal data and, if necessary, have it amended or deleted or restricted. In certain instances you may have the right to the portability of your data. You can also ask us to not send marketing communications and not to use your personal data when we carry out profiling for direct marketing purposes.
            </p>
            <p>
              You can opt out of receiving email newsletters and other marketing communications by following the opt-out instructions provided to you in those emails. Transactional account messages will be unaffected if you opt-out from marketing communications.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Your California Privacy Rights</h2>
            <p>
              California residents who provide personal information in obtaining products or services for personal, family or household use are entitled to request and obtain from us, once per calendar year, information about the customer information we shared, if any, with other businesses for their own direct marketing uses.
            </p>
            <p>
              To obtain this information, please email us at <a href="mailto:support@file-convert-hub.com">support@file-convert-hub.com</a> with the subject line "Request for California Privacy Information"; we will send you a reply e-mail containing the requested information.
            </p>
            <p>
              <strong>California Do Not Track (DNT):</strong> Because there is currently no industry or legal standard for recognizing or honoring DNT, we do not currently respond to DNT signals.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Complaints</h2>
            <p>
              Should you wish to raise a concern about our use of your information (and without prejudice to any other rights you may have), you have the right to do so with your local supervisory authority, although we hope that we can assist with any queries or concerns you may have about our use of your personal data.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes</h2>
            <p>
              FileConvertHub may periodically update this policy. We may notify you about significant changes in the way we treat personal information by placing a prominent notice on our website so please check back occasionally to ensure that you agree with the changes. If you do not, do not use the website or its services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Questions</h2>
            <p>
              Any questions about this Privacy Policy should be addressed to this address: <a href="mailto:support@file-convert-hub.com">support@file-convert-hub.com</a>.
            </p>
          </section>

          <section className="privacy-section">
            <p className="effective-date">
              <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
